// Import scheduling fetch utility
import { schedulingFetchGet } from 'app/utils/request/scheduling/fetch';
import { isEmpty } from 'app/utils';

// Helper function to get high-quality image URLs by removing thumbnail prefixes
export const getHighQualityImageUrl = (url: string | undefined): string | undefined => {
  if (!url) {
    return url;
  }

  // Fix pixelated images: Remove 'square_' prefix from CloudFront URLs to get full-size originals
  if (url.includes('cloudfront.net') && url.includes('/square_')) {
    // Replace /square_{hash}.jpg with /{hash}.jpg to get original image
    return url.replace(/\/square_([^/]+)$/, '/$1');
  }

  return url;
};

// Import all coach data statically
import alinaProfile from '../data/alina-carpenter/profile.json';
import alinaSessions from '../data/alina-carpenter/sessions.json';
import alinaPackages from '../data/alina-carpenter/packages.json';
import alinaEvents from '../data/alina-carpenter/events.json';

import johnProfile from '../data/john-smith/profile.json';
import johnSessions from '../data/john-smith/sessions.json';
import johnPackages from '../data/john-smith/packages.json';
import johnEvents from '../data/john-smith/events.json';

import mariaProfile from '../data/maria-rodriguez/profile.json';
import mariaSessions from '../data/maria-rodriguez/sessions.json';
import mariaPackages from '../data/maria-rodriguez/packages.json';
import mariaEvents from '../data/maria-rodriguez/events.json';

import davidProfile from '../data/david-chen/profile.json';
import davidSessions from '../data/david-chen/sessions.json';
import davidPackages from '../data/david-chen/packages.json';
import davidEvents from '../data/david-chen/events.json';

import sarahProfile from '../data/sarah-williams/profile.json';
import sarahSessions from '../data/sarah-williams/sessions.json';
import sarahPackages from '../data/sarah-williams/packages.json';
import sarahEvents from '../data/sarah-williams/events.json';

import jamesProfile from '../data/james-brown/profile.json';
import jamesSessions from '../data/james-brown/sessions.json';
import jamesPackages from '../data/james-brown/packages.json';
import jamesEvents from '../data/james-brown/events.json';

import michaelProfile from '../data/michael-torres/profile.json';
import michaelSessions from '../data/michael-torres/sessions.json';
import michaelPackages from '../data/michael-torres/packages.json';
import michaelEvents from '../data/michael-torres/events.json';

// Import facility data
import centralGolfEvents from '../data/facilities/central-golf-club/events.json';
import riversideEvents from '../data/facilities/riverside-academy/events.json';
import lakesideEvents from '../data/facilities/lakeside-country-club/events.json';

// Type definitions
export interface Location {
  id: number;
  name: string;
  address: string;
  coordinates: {
    lat: number;
    lon: number;
  };
}

// Real API response interfaces
export interface FacilityAddress {
  id: number;
  street1: string;
  street2: string | null;
  city: string;
  state: string;
  zip: string;
  country: string;
}

export interface LessonRate {
  id: number;
  duration: string;
  rate: number;
  defaultRate: boolean;
  title: string;
  location?: string;
  active: boolean;
  collectType: string;
  onlineDefault: boolean;
  durationInMinutes: number;
  timeTables: any[];
  orderNumber?: number | null;
  description?: string | null;
  hidePrice?: boolean;
}

export interface CoachProfileAPI {
  externalId: string;
  firstName: string;
  lastName: string;
  email: string;
  profilePicture: string | null;
  timeZone: string;
  currency: string;
  currencySymbol: string;
  lessonRates: LessonRate[];
  /** When the booking POLICY opens. A date-picker bound, not a free slot. */
  earliestBookingDate: string | null;
  latestBookingDate: string | null;
  /**
   * The coach's next bookable slot, read from their real availability, or null
   * when they have none in the next 60 days. This is the one to show a visitor
   * (CN-2764).
   */
  nextAvailableAt?: string | null;
  allowsPayments: boolean;
  calendarUuid: string;
  publicLandingPage: boolean;
  publicCalendar?: boolean;
  orderNumber: number | null;
  /** The coach's title at this facility, e.g. "PGA Class A" — set in Admin Settings → Users. */
  title?: string | null;
}

export interface FacilityAPIResponse {
  id: string;
  name: string;
  address: FacilityAddress;
  timeZone: string;
  currency: string;
  logoUrl: string | null;
  fullAddress: string;
  uuid: string;
  coachProfiles: CoachProfileAPI[];
  publicCalendarUrl: string;
  publicProgramsUrl: string;
  publicLogoUrl?: string;
  publicCoverUrl?: string;
  publicCalendar?: boolean;
  publicLandingPage?: boolean;
}

export interface Contact {
  phone: string;
  email: string;
  website: string;
}

export interface Facility {
  id: number;
  slug: string;
  name: string;
  logo?: string;
  backgroundImage?: string;
}

export interface Profile {
  username: string;
  firstName: string;
  lastName: string;
  role: string;
  avatar: string;
  about: string;
  contact: Contact;
  locations: Location[];
  backgroundImage: string | undefined;
  facility: Facility | undefined;
  awards?: string;
  address?: string;
  city?: string;
  country?: string;
  zip?: string;
  currency?: string;
  timeZone?: string;
}

export interface Session {
  id: number;
  title: string;
  description: string;
  duration: number;
  price: number;
  // CN-2711: the coach asked for this option's price to stay off the page.
  hidePrice?: boolean;
}

export interface Package {
  id: number;
  title: string;
  description: string;

  price: number;
  currency: string;
  productType: "product" | "subscription";
  enabled: boolean;

  sessions: number | null;
  recurring: string | null;
  billStartDate?: string | null;
  billingStartOn?: string | null;
  maxBillingCycles?: number | null;
  pastDue?: number | null;
  subscribed?: boolean;

  stripeProdId?: string;
  stripePriceId?: string;

  collectType: string;

  rateLinks: {
    duration: string;
    title: string;
    quantity: number | null;
  }[];
}

export interface Event {
  id: number;
  title: string;
  description: string;
  location: string;
  image: string;
  price: number;
  priceCurrency: string;
  isFree: boolean;
  date: string;
  coachName: string | undefined;
  coachAvatar: string | undefined;
}

export interface Coach {
  id: string | number;
  username: string;
  name: string;
  role: string;
  /** IANA zone, for the header card's "(GMT-05:00) Eastern Time" line. */
  timeZone?: string;
  avatar: string | undefined;
  location: string;
  nextSession: string;
  /**
   * Cheapest quotable option, or null when there is nothing to quote — every
   * bookable option hides its price, or the coach has none. Not 0: a free
   * option is a real thing, and "From $0" is the right thing to show for it.
   */
  priceFrom: number | null;
  currency: string;
  calendarUuid: string;
  about: string;
  email: string;
  phone: string;
  website: string;
  publicLandingPage: boolean;
  publicCalendar?: boolean;
  coverUrl: string;
  awards: string;
  address?: string;
  city?: string;
  country?: string;
  zip?: string;
}

export interface FacilityProfile {
  id: string | number;
  slug: string;
  name: string;
  about: string;
  logo: string | undefined;
  backgroundImage?: string;
  contact: Contact | undefined;
  address?: FacilityAddress;
  fullAddress?: string;
  timeZone?: string;
  currency?: string;
  publicCalendarUrl?: string;
  publicProgramsUrl?: string;
  publicCalendar?: boolean;
  publicLandingPage?: boolean;
}

interface APIResponse<T> {
  data: T;
  status: number;
}

// Helper function to format earliest booking date into friendly next session text
const formatNextSession = (dateString: string | null): string => {
  if (!dateString) {
    return 'Not available';
  }

  const sessionDate = new Date(dateString);
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const timeString = sessionDate.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  // Check if today
  if (sessionDate.toDateString() === now.toDateString()) {
    return `Today at ${timeString}`;
  }

  // Check if tomorrow
  if (sessionDate.toDateString() === tomorrow.toDateString()) {
    return `Tomorrow at ${timeString}`;
  }

  // Check if within this week
  const daysUntil = Math.floor((sessionDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (daysUntil < 7) {
    const dayName = sessionDate.toLocaleDateString('en-US', { weekday: 'long' });
    return `${dayName} at ${timeString}`;
  }

  // Otherwise show date
  return sessionDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  }) + ` at ${timeString}`;
};

// Transform FacilityAPIResponse to FacilityProfile
export const transformFacilityResponse = (apiResponse: FacilityAPIResponse): FacilityProfile => {
  return {
    id: apiResponse.id,
    slug: apiResponse.uuid,
    name: apiResponse.name,
    about: '',
    logo: apiResponse.publicLogoUrl,
    backgroundImage: apiResponse.publicCoverUrl,
    contact: undefined,
    address: apiResponse.address,
    fullAddress: apiResponse.fullAddress,
    timeZone: apiResponse.timeZone,
    currency: apiResponse.currency,
    publicCalendarUrl: apiResponse.publicCalendarUrl,
    publicCalendar: apiResponse.publicCalendar,
    publicProgramsUrl: apiResponse.publicProgramsUrl,
    publicLandingPage: apiResponse.publicLandingPage,
  };
};

// Transform CoachProfileAPI[] to Coach[]
export const transformCoachesResponse = (
  coachProfiles: CoachProfileAPI[],
  facilityName: string
): Coach[] => {
  const sorted = [...coachProfiles].sort((a, b) => {
    const orderA = a.orderNumber ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.orderNumber ?? Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) { return orderA - orderB; }
    return (a.firstName || "").localeCompare(b.firstName || "");
  });
  return sorted.map((coach) => {
    // CN-2711: "From <price>" must not leak the price of an option that hides it.
    const pricedLessonRates = (coach.lessonRates || []).filter(r => r.active && !r.hidePrice);
    const priceFrom = pricedLessonRates.length > 0
      ? Math.min(...pricedLessonRates.map(r => r.rate))
      : null;

    const name = `${coach.firstName} ${coach.lastName}`.trim();
    const slug = name.toLowerCase().replace(/\s+/g, '-');

    return {
      id: coach.externalId,
      username: slug,
      name,
      // Their title at this facility, where they have one (CN-2693).
      role: (coach.title || '').trim() || 'Coach',
      timeZone: coach.timeZone,
      avatar: coach.profilePicture || undefined,
      location: facilityName,
      nextSession: formatNextSession(coach.nextAvailableAt),
      priceFrom,
      currency: coach.currency,
      calendarUuid: coach.calendarUuid,
      about: '',
      email: '',
      phone: '',
      website: '',
      publicLandingPage: coach.publicLandingPage,
      publicCalendar: coach.publicCalendar,
      coverUrl: '',
      awards: '',
    };
  });
};

// Transform LessonRate[] to Session[]
export const transformLessonRatesToSessions = (lessonRates: LessonRate[]): Session[] => {
  if (!lessonRates || !Array.isArray(lessonRates)) {
    return [];
  }

  return lessonRates
    .filter(rate => rate.active)
    .sort((a, b) => {
      // Show the coach's exact configured order (unordered rates last). The
      // positional default is simply whatever the coach put first.
      const orderA = a.orderNumber ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.orderNumber ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    })
    .map(rate => ({
      id: rate.id,
      title: rate.title,
      // CFTB-3416: a description replaces the generated "<duration> session"
      // wording, but it must not swallow the location — the landing page still
      // reads "<description> at <location>". Location-only options keep their
      // existing "<duration> session at <location>" text.
      description: [
        rate.description?.trim() || `${rate.duration} session`,
        rate.location ? `at ${rate.location}` : null,
      ]
        .filter(Boolean)
        .join(" "),
      duration: rate.durationInMinutes,
      price: rate.rate,
      hidePrice: !!rate.hidePrice,
    }));
};

// Helper function to get event image with fallback
const getEventImage = (imageUrl: string | undefined): string => {
  if (isEmpty(imageUrl) || imageUrl?.includes('/assets/fallback/')) {
    return 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=400&h=300&fit=crop';
  }
  return imageUrl!;
};

// Programs API response interface
/**
 * A clinic's coaches, as the programmes index sends them
 * (`ProgramPresenter#coaches_info`). It carries the picture, which
 * `coaches_full_names` does not.
 */
export interface ProgramCoachAPI {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  profilePicture?: string | null;
}

export interface ProgramAPI {
  id: number;
  title: string;
  capacity: number;
  spots_booked: number;
  status: string;
  start_time: string;
  end_time: string;
  start: string;
  end: string;
  externalThumbnailUrl: string;
  external_header_url: string;
  registration_status: string;
  registration_status_code: string;
  collect_type: string;
  price: number;
  priceCurrency: string;
  free?: boolean;
  description: string;
  coachesFullNames: string[];
  coaches?: ProgramCoachAPI[];
}

// Transform ProgramAPI to Event
export const transformProgramToEvent = (
  program: ProgramAPI,
  facilityName: string,
  coachProfiles?: CoachProfileAPI[]
): Event => {

  const coachCount = program.coachesFullNames?.length || 0;

  let coachName: string | undefined;
  let coachAvatar: string | undefined;

  /**
   * The picture comes off the programme itself where the payload has it — the
   * index sends `coaches` with each one's `profile_picture`. `coachProfiles` is
   * the older way in, kept for the callers that hold a facility's list and a
   * programme payload that predates it.
   */
  const pictureFor = (fullName: string | undefined): string | undefined => {
    const onProgram = program.coaches?.find(
      (c) => (c.fullName || `${c.firstName} ${c.lastName}`).trim() === fullName,
    );
    if (onProgram?.profilePicture) {
      return onProgram.profilePicture;
    }

    const onFacility = coachProfiles?.find(
      (cp) => `${cp.firstName} ${cp.lastName}` === fullName,
    );
    return onFacility?.profilePicture || undefined;
  };

  if (coachCount === 1) {
    coachName = program.coachesFullNames[0];
    coachAvatar = pictureFor(coachName);
  } else if (coachCount > 1) {
    coachName = `${coachCount} Coaches`;

    // Any of them with a picture: the row says how many are running the clinic,
    // and one face beside that count is what the frame draws.
    coachAvatar =
      program.coachesFullNames.map(pictureFor).find(Boolean) ||
      coachProfiles?.find(
        (cp) =>
          cp.profilePicture &&
          program.coachesFullNames.includes(`${cp.firstName} ${cp.lastName}`),
      )?.profilePicture ||
      undefined;
  }

  return {
    id: program.id,
    title: program.title,
    description: program.description,
    location: facilityName,
    image: getEventImage(program?.externalThumbnailUrl),
    price: program.price,
    priceCurrency: program.priceCurrency,
    isFree: program.free ?? false,
    date: program.start,
    coachName,
    coachAvatar
  };
};

export const transformStripeProductToPackage = (stripeProduct: any): Package => {
  return {
    id: stripeProduct.id,
    title: stripeProduct.title,
    description: stripeProduct.description || '',
    price: parseFloat(stripeProduct.price?.replace(/,/g, '') || '0'),
    currency: stripeProduct.currency || 'USD',
    productType: stripeProduct.productType,
    enabled: stripeProduct.enabled,

    sessions: stripeProduct.numberOfSessions ?? stripeProduct.number_of_sessions ?? 0,

    recurring: stripeProduct.recurring ?? null,
    billStartDate: stripeProduct.billStartDate ?? null,
    billingStartOn: stripeProduct.billingStartOn ?? null,
    maxBillingCycles: stripeProduct.maxBillingCycles ?? null,
    pastDue: stripeProduct.pastDue ?? null,
    subscribed: stripeProduct.subscribed ?? false,

    stripeProdId: stripeProduct.stripeProdId,
    stripePriceId: stripeProduct.stripePriceId,

    // Backend contract: `collect_type` is always present ("offline" | "online" |
    // "both"), so the "online" fallback below is purely defensive and never fires.
    collectType: stripeProduct.collectType ?? stripeProduct.collect_type ?? "online",

    rateLinks: stripeProduct.rateLinks?.map((rate: any) => ({
      duration: rate.duration,
      title: rate.title,
      quantity: rate.quantity,
    })) || []
  };
};


// Mock API service that simulates network requests
const API_DELAY = 1000; // Simulate network delay in milliseconds

// Helper function to simulate API call
const mockFetch = <T>(data: T, delay: number = API_DELAY): Promise<APIResponse<T>> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({ data, status: 200 });
    }, delay);
  });
};

// List of all facilities
interface FacilityListItem {
  id: number;
  slug: string;
  name: string;
}

const FACILITIES: FacilityListItem[] = [
  { id: 1, slug: 'central-golf-club', name: 'Central Golf Club' },
  { id: 2, slug: 'riverside-academy', name: 'Riverside Golf Academy' },
  { id: 3, slug: 'lakeside-country-club', name: 'Lakeside Country Club' }
];

// Data mappings
const PROFILE_DATA: { [key: string]: typeof alinaProfile } = {
  'alina-carpenter': alinaProfile,
  'john-smith': johnProfile,
  'maria-rodriguez': mariaProfile,
  'david-chen': davidProfile,
  'sarah-williams': sarahProfile,
  'james-brown': jamesProfile,
  'michael-torres': michaelProfile
};

const SESSIONS_DATA: { [key: string]: typeof alinaSessions } = {
  'alina-carpenter': alinaSessions,
  'john-smith': johnSessions,
  'maria-rodriguez': mariaSessions,
  'david-chen': davidSessions,
  'sarah-williams': sarahSessions,
  'james-brown': jamesSessions,
  'michael-torres': michaelSessions
};

const PACKAGES_DATA: { [key: string]: typeof alinaPackages } = {
  'alina-carpenter': alinaPackages,
  'john-smith': johnPackages,
  'maria-rodriguez': mariaPackages,
  'david-chen': davidPackages,
  'sarah-williams': sarahPackages,
  'james-brown': jamesPackages,
  'michael-torres': michaelPackages
};

const EVENTS_DATA: { [key: string]: any } = {
  'alina-carpenter': alinaEvents,
  'john-smith': johnEvents,
  'maria-rodriguez': mariaEvents,
  'david-chen': davidEvents,
  'sarah-williams': sarahEvents,
  'james-brown': jamesEvents,
  'michael-torres': michaelEvents
};

// API endpoints
const API = {
  // Get coach profile and locations by username
  getCoachProfile: async (username: string = 'alina-carpenter'): Promise<APIResponse<{ profile: Profile }>> => {
    try {
      const profile = PROFILE_DATA[username];
      if (!profile) {
        throw new Error(`Profile not found for username: ${username}`);
      }
      return mockFetch({ profile: profile as Profile });
    } catch (error) {
      console.error('Error loading coach profile:', error);
      throw error;
    }
  },

  // Get private sessions list by username
  getPrivateSessions: async (username: string = 'alina-carpenter'): Promise<APIResponse<{ sessions: Session[]; totalCount: number }>> => {
    try {
      const data = SESSIONS_DATA[username];
      if (!data) {
        // Return empty data for real coaches (not in mock data)
        return mockFetch({ sessions: [], totalCount: 0 });
      }
      return mockFetch(data as { sessions: Session[]; totalCount: number });
    } catch (error) {
      console.error('Error loading private sessions:', error);
      throw error;
    }
  },

  // Get packages and subscriptions by username
  getPackages: async (username: string = 'alina-carpenter'): Promise<APIResponse<{ packages: Package[] }>> => {
    try {
      const data = PACKAGES_DATA[username];
      if (!data) {
        // Return empty data for real coaches (not in mock data)
        return mockFetch({ packages: [] });
      }
      return mockFetch({
        packages: data.packages as unknown as Package[]
      });
    } catch (error) {
      console.error('Error loading packages:', error);
      throw error;
    }
  },

  // Get events and clinics by username
  getEvents: async (username: string = 'alina-carpenter'): Promise<APIResponse<{ events: Event[] }>> => {
    try {
      const data = EVENTS_DATA[username];
      if (!data) {
        // Return empty data for real coaches (not in mock data)
        return mockFetch({ events: [] });
      }
      return mockFetch(data as { events: Event[] });
    } catch (error) {
      console.error('Error loading events:', error);
      throw error;
    }
  },

  // Facility endpoints
  // Get list of all facilities
  getAllFacilities: async (): Promise<APIResponse<{ facilities: FacilityListItem[] }>> => {
    return mockFetch({ facilities: FACILITIES }, 100);
  },

  // Get facility profile by ID (uses real API)
  getFacilityProfile: async (facilityId: string = '1'): Promise<APIResponse<{ facility: FacilityProfile }>> => {
    try {
      const response = await schedulingFetchGet<FacilityAPIResponse>(
        `facilities/${facilityId}`,
        {},
        {},
        undefined,
        true  // public_url = true
      );

      // Handle the response as a single object (not an array)
      const facilityData = Array.isArray(response) ? response[0] : response;
      const facility = transformFacilityResponse(facilityData);
      return { data: { facility }, status: 200 };
    } catch (error) {
      console.error('Error loading facility profile:', error);
      throw error;
    }
  },

  // Get facility coaches by ID (uses real API)
  getFacilityCoaches: async (facilityId: string = '1'): Promise<APIResponse<{ coaches: Coach[] }>> => {
    try {
      const response = await schedulingFetchGet<FacilityAPIResponse>(
        `facilities/${facilityId}`,
        {},
        {},
        undefined,
        true  // public_url = true
      );

      // Handle the response as a single object (not an array)
      const facilityData = Array.isArray(response) ? response[0] : response;
      const coaches = transformCoachesResponse(
        facilityData.coachProfiles || [],
        facilityData.name
      );
      return { data: { coaches }, status: 200 };
    } catch (error) {
      console.error('Error loading facility coaches:', error);
      throw error;
    }
  },

  // Get facility events by ID (still mocked - returns empty for real facility IDs)
  getFacilityEvents: async (facilityId: string = '1'): Promise<APIResponse<any>> => {
    try {
      // Keep old mock behavior for specific mock IDs only
      const idToSlugMap: { [key: string]: string } = {
        '1': 'central-golf-club',
        '2': 'riverside-academy',
        '3': 'lakeside-country-club'
      };
      const slug = idToSlugMap[facilityId];

      // Return empty events for any real facility ID (not in mock data)
      if (!slug) {
        return mockFetch({ events: [] });
      }
      const eventsData: { [key: string]: any } = {
        'central-golf-club': centralGolfEvents,
        'riverside-academy': riversideEvents,
        'lakeside-country-club': lakesideEvents
      };
      const data = eventsData[slug];
      if (!data) {
        throw new Error(`Events not found for facility: ${facilityId}`);
      }
      return mockFetch(data);
    } catch (error) {
      console.error('Error loading facility events:', error);
      throw error;
    }
  }
};

export default API;
